import React, { FormEvent, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CalendarCheck, CalendarDays, CheckCircle2, Clock3, Cylinder, Gauge, MoreHorizontal, Search, Truck, UserRound, Wrench, Zap } from 'lucide-react';
type AppointmentForm = {
  service: string;
  year: string;
  makeModel: string;
  engine: string;
  name: string;
  phone: string;
  email: string;
  date: string;
  time: string;
  notes: string;
};
type FieldName = keyof AppointmentForm;
type ServiceOption = {
  title: string;
  icon: React.ReactNode;
};
const initialForm: AppointmentForm = {
  service: '',
  year: '',
  makeModel: '',
  engine: '',
  name: '',
  phone: '',
  email: '',
  date: '',
  time: '',
  notes: ''
};
const services: ServiceOption[] = [{
  title: 'Oil Change & Preventive Maintenance',
  icon: <Wrench data-id="element-29" data-mp="13gj6fdqjz8jz" />
}, {
  title: 'Diagnostic Inspection',
  icon: <Search data-id="element-30" data-mp="17imia3h4wf5s" />
}, {
  title: 'Powerstroke Repair',
  icon: <Gauge data-id="element-31" data-mp="17hbguab5vjhu" />
}, {
  title: 'Engine Reconditioning',
  icon: <Cylinder data-id="element-32" data-mp="1x4bzyabiukhj" />
}, {
  title: 'Electrical Diagnostics',
  icon: <Zap data-id="element-33" data-mp="1x30tu3ult0n9" />
}, {
  title: 'Other Service',
  icon: <MoreHorizontal data-id="element-34" data-mp="1s47j2j6pbnqs" />
}];
const timeOptions = ['8:00 AM – 10:00 AM', '10:00 AM – 12:00 PM', '12:00 PM – 2:00 PM', '2:00 PM – 4:00 PM'];
export function Appointment() {
  const [form, setForm] = useState<AppointmentForm>(initialForm);
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const minimumDate = useMemo(() => getLocalDate(), []);
  const availableDates = useMemo(() => getAvailableAppointmentDates(), []);
  const formIsComplete = requiredFields.every(field => !getFieldError(field, form, minimumDate));
  function updateField(field: FieldName, value: string) {
    setForm(current => ({
      ...current,
      [field]: value
    }));
  }
  function handleBlur(field: FieldName) {
    setTouched(current => ({
      ...current,
      [field]: true
    }));
  }
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTouched(markRequiredFieldsTouched());
    if (!formIsComplete) return;
    setIsSubmitting(true);
    window.setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 650);
  }
  function resetForm() {
    setForm(initialForm);
    setTouched({});
    setIsSubmitted(false);
  }
  if (isSubmitted) {
    return <section id="appointment" className="scroll-mt-24 py-24 bg-slate-100" data-id="element-35" data-mp="zir68x5evsxb">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-36" data-mp="14gap6ad20tvl">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="bg-white border border-slate-200 border-t-4 border-t-orange rounded-xl shadow-xl p-8 sm:p-12 text-center" role="status" aria-live="polite" data-id="element-37" data-mp="pspi65d26aie">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-orange/10 flex items-center justify-center" data-id="element-38" data-mp="1n9ehnsejd0om">
              <CheckCircle2 className="w-9 h-9 text-orange" strokeWidth={2} data-id="element-39" data-mp="37w4g7399twi4" />
            </div>
            <p className="text-orange font-bold uppercase tracking-[0.18em] text-sm mb-3" data-id="element-40" data-mp="32yvyf3s1buhe">
              Request received
            </p>
            <h2 className="font-display text-4xl sm:text-5xl text-dark mb-4" data-id="element-41" data-mp="3cp7f7qwthy5p">
              We’ll Confirm Your Appointment.
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed max-w-xl mx-auto mb-8" data-id="element-42" data-mp="2p062hrgyc0um">
              Thanks, {form.name}. Your request for {form.service.toLowerCase()} on{' '}
              {formatRequestDate(form.date)} during the {form.time} window has been sent.
              Our team will contact you to confirm the requested time and discuss your truck.
            </p>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-5 text-left max-w-md mx-auto mb-8" data-id="element-43" data-mp="d90yjtfkhkfl">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3" data-id="element-44" data-mp="89xrk3g1144z">
                Request summary
              </p>
              <dl className="space-y-2 text-sm" data-id="element-45" data-mp="13na3u258es9z">
                <div className="flex justify-between gap-4" data-id="element-46" data-mp="18l1smnakxy9y">
                  <dt className="text-slate-500" data-id="element-47" data-mp="1djd3n1cm2etr">Vehicle</dt>
                  <dd className="text-dark font-semibold text-right" data-id="element-48" data-mp="1dhs7x8ek8pub">
                    {form.year} {form.makeModel}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-49" data-mp="1mkjmvi6na3qp">
                  <dt className="text-slate-500" data-id="element-50" data-mp="1mhxddd071e5u">Requested time</dt>
                  <dd className="text-dark font-semibold text-right" data-id="element-51" data-mp="1rh9zfvj8qh7d">{form.time}</dd>
                </div>
              </dl>
            </div>
            <button type="button" onClick={resetForm} className="inline-flex items-center justify-center px-6 py-3 text-sm font-bold text-dark border border-slate-300 rounded-md hover:border-orange hover:text-orange transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2" data-id="element-52" data-mp="218eh530awg5w">
              Submit another request
            </button>
          </motion.div>
        </div>
      </section>;
  }
  return <section id="appointment" className="scroll-mt-24 py-24 bg-slate-100" data-id="element-53" data-mp="3lpx3jhggq2fq">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-54" data-mp="3grlwfehjy617">
        <div className="max-w-3xl mb-12" data-id="element-55" data-mp="3gtgduitm7g0j">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-orange/25 bg-orange/10 text-orange text-sm font-bold uppercase tracking-widest mb-5" data-id="element-56" data-mp="i9q8fzc45dik">
            <CalendarCheck className="w-4 h-4" data-id="element-57" data-mp="8k6sp2jp5zrx" />
            Appointment request
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-dark leading-tight mb-4" data-id="element-58" data-mp="8hu3q5fiup5d">
            Book Service for Your <span className="text-orange" data-id="element-59" data-mp="1107q98veqop5">Powerstroke.</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl" data-id="element-60" data-mp="112kda0g0vp92">
            Tell us what your truck needs and when you’d like to come in. We’ll review your request and personally confirm your appointment time.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-8 items-start" data-id="element-61" data-mp="2ujghbuugp6ep">
          <form onSubmit={handleSubmit} noValidate className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden" data-id="element-62" data-mp="2gslcctn339ge">
            <div className="p-6 sm:p-8 border-b border-slate-200" data-id="element-63" data-mp="2lp221s9xlvvl">
              <FormSectionHeading icon={<Wrench data-id="element-65" data-mp="26s2o7mohl6so" />} number="01" title="Choose your service" description="Select the primary reason for your visit." data-id="element-64" data-mp="26skpixq0ab7g" />
              <fieldset className="mt-6" data-id="element-66" data-mp="2bqci7tyh61cp">
                <legend className="sr-only" data-id="element-67" data-mp="1xtpm1tecjf7u">Requested service</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-id="element-68" data-mp="22s0qtppsazj7">
                  {services.map(service => {
                  const isSelected = form.service === service.title;
                  return <button key={service.title} type="button" onClick={() => {
                    updateField('service', service.title);
                    handleBlur('service');
                  }} aria-pressed={isSelected} className={`min-h-[82px] text-left rounded-lg border p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2 ${isSelected ? 'border-orange bg-orange/10 text-dark' : 'border-slate-200 text-slate-600 hover:border-orange/60 hover:bg-slate-50'}`} data-id="element-69" data-mp="zmf58du2rb8">
                        <span className={`mb-2 block ${isSelected ? 'text-orange' : 'text-slate-500'}`} data-id="element-70" data-mp="yvhwxtf6gnpr">
                          {React.cloneElement(service.icon as React.ReactElement, {
                        className: 'w-5 h-5',
                        strokeWidth: 2
                      })}
                        </span>
                        <span className="block font-bold text-sm leading-snug" data-id="element-71" data-mp="3p700hjthoc32">{service.title}</span>
                      </button>;
                })}
                </div>
                <FieldError error={showError('service', form, touched, minimumDate)} data-id="element-72" data-mp="2r5jhqmt1v72" />
              </fieldset>
            </div>

            <div className="p-6 sm:p-8 border-b border-slate-200" data-id="element-73" data-mp="6rfmqvj7q7i8">
              <FormSectionHeading icon={<Truck data-id="element-75" data-mp="368dvhtuuq0tv" />} number="02" title="Tell us about your truck" description="A few details help us prepare for your visit." data-id="element-74" data-mp="367vsmedooocz" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6" data-id="element-76" data-mp="3b6z0e6l1t8on">
                <FormField label="Vehicle year" field="year" required error={showError('year', form, touched, minimumDate)} data-id="element-77" data-mp="3f797xnoho75m">
                  <input id="appointment-year" name="year" type="number" inputMode="numeric" min="1980" max={String(new Date().getFullYear() + 1)} placeholder="e.g. 2020" value={form.year} onChange={event => updateField('year', event.target.value)} onBlur={() => handleBlur('year')} aria-invalid={Boolean(showError('year', form, touched, minimumDate))} aria-describedby={errorId('year', form, touched, minimumDate)} className={inputClass(Boolean(showError('year', form, touched, minimumDate)))} data-id="element-78" data-mp="3k8oslemr8bnh" />
                </FormField>
                <FormField label="Make & model" field="makeModel" required error={showError('makeModel', form, touched, minimumDate)} data-id="element-79" data-mp="lxlfzb7e0eda">
                  <input id="appointment-makeModel" name="makeModel" type="text" placeholder="e.g. Ford F-250 Super Duty" value={form.makeModel} onChange={event => updateField('makeModel', event.target.value)} onBlur={() => handleBlur('makeModel')} aria-invalid={Boolean(showError('makeModel', form, touched, minimumDate))} aria-describedby={errorId('makeModel', form, touched, minimumDate)} className={inputClass(Boolean(showError('makeModel', form, touched, minimumDate)))} data-id="element-80" data-mp="cv3syujwrhp8" />
                </FormField>
                <FormField label="Powerstroke engine" field="engine" error={null} data-id="element-81" data-mp="317rdk43efce2">
                  <select id="appointment-engine" name="engine" value={form.engine} onChange={event => updateField('engine', event.target.value)} className={inputClass(false)} data-id="element-82" data-mp="2w84wtkod3e0i">
                    <option value="" data-id="element-83" data-mp="2ifovcob3zo1p">Select if known (optional)</option>
                    <option value="7.3L Powerstroke" data-id="element-84" data-mp="2dh554bphfq3j">7.3L Powerstroke</option>
                    <option value="6.0L Powerstroke" data-id="element-85" data-mp="2df135zb5915w">6.0L Powerstroke</option>
                    <option value="6.4L Powerstroke" data-id="element-86" data-mp="syb5z7xwdlqg">6.4L Powerstroke</option>
                    <option value="6.7L Powerstroke" data-id="element-87" data-mp="xq2z9q8fu5ka">6.7L Powerstroke</option>
                    <option value="Other diesel engine" data-id="element-88" data-mp="jyod5aj8ho90">Other diesel engine</option>
                  </select>
                </FormField>
              </div>
            </div>

            <div className="p-6 sm:p-8 border-b border-slate-200" data-id="element-89" data-mp="nxndehu0swu4">
              <FormSectionHeading icon={<UserRound data-id="element-91" data-mp="1bnhnwsqva9b7" />} number="03" title="Your contact details" description="We’ll use these details only to follow up on your request." data-id="element-90" data-mp="1bq3x0wteowvj" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6" data-id="element-92" data-mp="12pp9rrwvt7pe">
                <FormField label="Full name" field="name" required error={showError('name', form, touched, minimumDate)} data-id="element-93" data-mp="17nqro9ikn0za">
                  <input id="appointment-name" name="name" type="text" autoComplete="name" placeholder="Your name" value={form.name} onChange={event => updateField('name', event.target.value)} onBlur={() => handleBlur('name')} aria-invalid={Boolean(showError('name', form, touched, minimumDate))} aria-describedby={errorId('name', form, touched, minimumDate)} className={inputClass(Boolean(showError('name', form, touched, minimumDate)))} data-id="element-94" data-mp="3nevevbi39bf5" />
                </FormField>
                <FormField label="Phone number" field="phone" required error={showError('phone', form, touched, minimumDate)} data-id="element-95" data-mp="1a7dnttb91jfj">
                  <input id="appointment-phone" name="phone" type="tel" autoComplete="tel" placeholder="(954) 871-0290" value={form.phone} onChange={event => updateField('phone', event.target.value)} onBlur={() => handleBlur('phone')} aria-invalid={Boolean(showError('phone', form, touched, minimumDate))} aria-describedby={errorId('phone', form, touched, minimumDate)} className={inputClass(Boolean(showError('phone', form, touched, minimumDate)))} data-id="element-96" data-mp="159ly95y2u9ei" />
                </FormField>
                <FormField label="Email address" field="email" required error={showError('email', form, touched, minimumDate)} data-id="element-97" data-mp="v7878ta0kfdu">
                  <input id="appointment-email" name="email" type="email" autoComplete="email" placeholder="you@email.com" value={form.email} onChange={event => updateField('email', event.target.value)} onBlur={() => handleBlur('email')} aria-invalid={Boolean(showError('email', form, touched, minimumDate))} aria-describedby={errorId('email', form, touched, minimumDate)} className={inputClass(Boolean(showError('email', form, touched, minimumDate)))} data-id="element-98" data-mp="3dplnppbtp6r" />
                </FormField>
              </div>
            </div>

            <div className="p-6 sm:p-8" data-id="element-99" data-mp="1vbhb3qp7kv5f">
              <FormSectionHeading icon={<CalendarDays data-id="element-101" data-mp="1rb76oih2ecso" />} number="04" title="Requested appointment" description="Choose your preferred day and time window. We’ll confirm availability with you." data-id="element-100" data-mp="1rbgxg89xonzf" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6" data-id="element-102" data-mp="20d6ohczwlkwo">
                <FormField label="Preferred date" field="date" required error={showError('date', form, touched, minimumDate)} data-id="element-103" data-mp="20au6izu5bajm">
                  <select id="appointment-date" name="date" value={form.date} onChange={event => updateField('date', event.target.value)} onBlur={() => handleBlur('date')} aria-invalid={Boolean(showError('date', form, touched, minimumDate))} aria-describedby={errorId('date', form, touched, minimumDate)} className={inputClass(Boolean(showError('date', form, touched, minimumDate)))} data-id="element-104" data-mp="2a1yf9ht89z60">
                    <option value="" data-id="element-105" data-mp="2tqmcacwjgf1f">Select an available date</option>
                    {availableDates.map(date => <option key={date} value={date} data-id="element-106" data-mp="2jxyjdt8oiv7z">
                        {formatAvailableDate(date)}
                      </option>)}
                  </select>
                  <p className="mt-2 text-xs text-slate-500" data-id="element-107" data-mp="2a0akcr41j1k0">Appointments are available Wednesday through Sunday.</p>
                </FormField>
                <FormField label="Preferred time" field="time" required error={showError('time', form, touched, minimumDate)} data-id="element-108" data-mp="2f05dz2f7a58r">
                  <select id="appointment-time" name="time" value={form.time} onChange={event => updateField('time', event.target.value)} onBlur={() => handleBlur('time')} aria-invalid={Boolean(showError('time', form, touched, minimumDate))} aria-describedby={errorId('time', form, touched, minimumDate)} className={inputClass(Boolean(showError('time', form, touched, minimumDate)))} data-id="element-109" data-mp="211p81dp5wiro">
                    <option value="" data-id="element-110" data-mp="ug1ndsb9pqy0">Select a time window</option>
                    {timeOptions.map(time => <option key={time} data-id="element-111" data-mp="pjba1cqul9ga">{time}</option>)}
                  </select>
                </FormField>
              </div>
              <FormField label="Notes for the technician" field="notes" error={null} className="mt-5" data-id="element-112" data-mp="letqneoegy61">
                <textarea id="appointment-notes" name="notes" rows={4} placeholder="Describe the issue, warning lights, recent work, or anything else we should know. (Optional)" value={form.notes} onChange={event => updateField('notes', event.target.value)} className={`${inputClass(false)} resize-y`} data-id="element-113" data-mp="gha9a5weo59v" />
              </FormField>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-slate-200 pt-6" data-id="element-114" data-mp="1ppn1yonmx89y">
                <p className="text-sm text-slate-500 flex items-start gap-2 max-w-md" data-id="element-115" data-mp="1uizpevgkv914">
                  <Clock3 className="w-4 h-4 text-orange shrink-0 mt-0.5" data-id="element-116" data-mp="1zfzscxi4rvo0" />
                  Your selected time is a request, not a live reservation. We’ll contact you to confirm.
                </p>
                <button type="submit" disabled={!formIsComplete || isSubmitting} className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-orange text-white font-bold shadow-lg shadow-orange/25 transition-all enabled:hover:bg-orange/90 enabled:hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2" data-id="element-117" data-mp="29cba430hpdpu">
                  {isSubmitting ? 'Sending request…' : 'Request Appointment'}
                  {!isSubmitting && <ArrowRight className="w-5 h-5" data-id="element-118" data-mp="8zvzlkqf7pgv" />}
                </button>
              </div>
            </div>
          </form>

          <aside className="lg:sticky lg:top-28 bg-dark rounded-xl p-7 text-white border-t-4 border-orange" data-id="element-119" data-mp="3dbrffkfs3i2r">
            <p className="text-orange font-bold text-sm uppercase tracking-widest mb-5" data-id="element-120" data-mp="38dybyz5v1266">What happens next</p>
            <ol className="space-y-6" data-id="element-121" data-mp="3me0os6zb95ek">
              {[['1', 'Send your request', 'Choose a service and preferred time window.'], ['2', 'We review the details', 'Our team prepares for your truck and service needs.'], ['3', 'We confirm with you', 'We’ll call or email to confirm a time that works.']].map(([number, title, description]) => <li key={number} className="flex gap-4" data-id="element-122" data-mp="153v0led4duxt">
                  <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-orange text-white font-bold text-sm" data-id="element-123" data-mp="195zri5c6odgr">{number}</span>
                  <div data-id="element-124" data-mp="v8b5uvaal2an">
                    <h3 className="font-bold mb-1" data-id="element-125" data-mp="v8tbujmrauvr">{title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed" data-id="element-126" data-mp="106392lge0au5">{description}</p>
                  </div>
                </li>)}
            </ol>
            <div className="mt-7 pt-6 border-t border-white/10" data-id="element-127" data-mp="1suqm5tqzftwm">
              <p className="text-sm text-slate-400 mb-2" data-id="element-128" data-mp="1sw1p5rclsxmo">Need immediate help?</p>
              <a href="tel:9548710290" className="font-display text-3xl text-white hover:text-orange transition-colors" data-id="element-129" data-mp="1e52nl0a6r9cl">
                954-871-0290
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}
type FormFieldProps = {
  label: string;
  field: FieldName;
  required?: boolean;
  error: string | null;
  className?: string;
  children: React.ReactNode;
};
function FormField({
  label,
  field,
  required = false,
  error,
  className = '',
  children
}: FormFieldProps) {
  return <div className={className} data-id="element-130" data-mp="b3utv35245yb">
      <label htmlFor={`appointment-${field}`} className="block text-sm font-bold text-dark mb-2" data-id="element-131" data-mp="3tgj6ju86v5qh">
        {label} {required && <span className="text-orange" aria-hidden="true" data-id="element-132" data-mp="28f693uwan16">*</span>}
      </label>
      {children}
      <FieldError error={error} id={error ? `appointment-${field}-error` : undefined} data-id="element-133" data-mp="3jgsbnrfpuopv" />
    </div>;
}
type FormSectionHeadingProps = {
  icon: React.ReactNode;
  number: string;
  title: string;
  description: string;
};
function FormSectionHeading({
  icon,
  number,
  title,
  description
}: FormSectionHeadingProps) {
  return <div className="flex items-start gap-4" data-id="element-134" data-mp="3fgi96or4ugfr">
      <div className="w-11 h-11 shrink-0 rounded-lg bg-orange/10 text-orange flex items-center justify-center" data-id="element-135" data-mp="18q21yn0kkgff">
        {React.cloneElement(icon as React.ReactElement, {
        className: 'w-5 h-5',
        strokeWidth: 2
      })}
      </div>
      <div data-id="element-136" data-mp="1ntvcgnb5w8wd">
        <p className="text-xs font-bold tracking-widest text-orange mb-1" data-id="element-137" data-mp="1ntlkwvagxtok">STEP {number}</p>
        <h3 className="text-xl font-bold text-dark" data-id="element-138" data-mp="1ius87530a6pg">{title}</h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-139" data-mp="1wkvm98rqeu3t">{description}</p>
      </div>
    </div>;
}
function FieldError({
  error,
  id
}: {
  error: string | null;
  id?: string;
}) {
  if (!error) return null;
  return <p id={id} className="mt-2 text-sm font-medium text-red-600" role="alert" data-id="element-140" data-mp="26j1mllxw3s6k">{error}</p>;
}
function inputClass(hasError: boolean) {
  return `w-full rounded-md border ${hasError ? 'border-red-500 bg-red-50' : 'border-slate-300 bg-white'} px-3.5 py-3 text-dark placeholder:text-slate-400 shadow-sm focus:border-orange focus:outline-none focus:ring-2 focus:ring-orange/20 transition-colors`;
}
function showError(field: FieldName, form: AppointmentForm, touched: Partial<Record<FieldName, boolean>>, minimumDate: string) {
  return touched[field] ? getFieldError(field, form, minimumDate) : null;
}
function errorId(field: FieldName, form: AppointmentForm, touched: Partial<Record<FieldName, boolean>>, minimumDate: string) {
  return showError(field, form, touched, minimumDate) ? `appointment-${field}-error` : undefined;
}
function getFieldError(field: FieldName, form: AppointmentForm, minimumDate: string): string | null {
  const value = form[field].trim();
  if (requiredFields.includes(field) && !value) return 'This field is required.';
  if (!value) return null;
  if (field === 'year') {
    const year = Number(value);
    const latestYear = new Date().getFullYear() + 1;
    if (!Number.isInteger(year) || year < 1980 || year > latestYear) return `Enter a year between 1980 and ${latestYear}.`;
  }
  if (field === 'email' && !/^\S+@\S+\.\S+$/.test(value)) return 'Enter a valid email address.';
  if (field === 'phone' && value.replace(/\D/g, '').length < 10) return 'Enter a valid 10-digit phone number.';
  if (field === 'date') {
    if (value < minimumDate) return 'Choose today or a future date.';
    if (isClosedDay(value)) return 'Appointments are unavailable on Mondays and Tuesdays.';
  }
  return null;
}
const requiredFields: FieldName[] = ['service', 'year', 'makeModel', 'name', 'phone', 'email', 'date', 'time'];
function markRequiredFieldsTouched(): Partial<Record<FieldName, boolean>> {
  return requiredFields.reduce<Partial<Record<FieldName, boolean>>>((fields, field) => {
    fields[field] = true;
    return fields;
  }, {});
}
function getLocalDate() {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().split('T')[0];
}
function formatRequestDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
}
function getAvailableAppointmentDates() {
  const dates: string[] = [];
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  while (dates.length < 60) {
    const dateValue = toDateInputValue(date);
    if (!isClosedDay(dateValue)) dates.push(dateValue);
    date.setDate(date.getDate() + 1);
  }
  return dates;
}
function isClosedDay(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  const weekday = new Date(year, month - 1, day).getDay();
  return weekday === 1 || weekday === 2;
}
function toDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
function formatAvailableDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}